/**
 * @author TEAM CMS
 *
 * A video playlist component that displays a grid of videos with playback controls.
 */
'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@svenskaspelab/ui/react/button';
import { useCallback, useMemo, useState } from 'react';
/**
 * Displays a list of videos in a grid format with an expandable description for the featured video.
 * Allows users to play videos, load more videos, and view additional videos on YouTube.
 *
 * @param props - The properties for the VideoPlaylist component.
 * @param props.videos - The list of videos to display.
 * @param props.availableVideos - The total number of available videos.
 * @param props.categoryKey - The key for the video category.
 * @param props.loadMoreButtonText - The text for the "Load More" button.
 * @param [props.maxVideosToShow] - The maximum number of videos to show initially.
 * @param [props.totalVideosToShow] - The initial number of videos to show.
 * @returns The rendered VideoPlaylist component.
 * @example
 * <VideoPlaylist
 *  availableVideos={50}
 *  categoryKey="my-category"
 *  loadMoreButtonText="Ladda fler"
 *  maxVideosToShow={20}
 *  totalVideosToShow={4}
 *  videos={[
 *    { id: '1', title: 'Video 1', thumbnail: 'thumbnail1.jpg', duration: 120, publishedAt: '2023-01-01', description: 'Description for video 1' },
 *    { id: '2', title: 'Video 2', thumbnail: 'thumbnail2.jpg', duration: 150, publishedAt: '2023-01-02', description: 'Description for video 2' },
 *    // ... more videos
 *  ]}
 * />
 */
export default function VideoPlaylist({ availableVideos, categoryKey, loadMoreButtonText = 'Load more videos', maxVideosToShow = 20, totalVideosToShow = 4, videos, }) {
    // State to manage the number of videos displayed
    const [totalVisibleVideos, setTotalVisibleVideos] = useState(totalVideosToShow);
    // State to manage expanded descriptions for videos
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
    // Early return for empty videos
    if (!videos || videos.length === 0) {
        return (_jsx("div", { className: "container mx-auto px-4 py-8", children: _jsx("p", { children: "Inga videor tillg\u00E4ngliga." }) }));
    }
    // Memoized calculations
    const displayedVideos = useMemo(() => videos.slice(1, totalVisibleVideos), [videos, totalVisibleVideos]);
    const featuredVideo = useMemo(() => videos[0], [videos]);
    const shouldShowLoadMoreButton = useMemo(() => totalVisibleVideos < maxVideosToShow, [totalVisibleVideos, maxVideosToShow]);
    const shouldShowYouTubeButton = useMemo(() => totalVisibleVideos === maxVideosToShow && availableVideos > maxVideosToShow, [totalVisibleVideos, maxVideosToShow, availableVideos]);
    /* Handler for playing a video
    const handleVideoPlay: (videoId: string) => void = useCallback((videoId: string): void => {
      TODO: Implement video playback logic here
      This could be a modal, a new page
      add analitics tracking, etc.
    }, []); */
    // Handler for loading more videos
    const handleLoadMoreVideos = useCallback(() => {
        setTotalVisibleVideos(maxVideosToShow);
    }, [maxVideosToShow]);
    // Toggle description visibility for expandable descriptions
    const toggleDescription = useCallback((videoId) => {
        setExpandedDescriptions((prev) => {
            const expandedSet = new Set(prev);
            if (expandedSet.has(videoId)) {
                expandedSet.delete(videoId);
            }
            else {
                expandedSet.add(videoId);
            }
            return expandedSet;
        });
    }, []);
    // Format duration helper
    const formatDuration = useCallback((duration) => {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }, []);
    // Parse links in text and convert them to clickable links
    const parseLinksInText = useCallback((text) => {
        // URL regex pattern that matches http(s) URLs
        const urlRegex = /(https?:\/\/\S+)/g;
        const parts = text.split(urlRegex);
        return parts.map((part, index) => {
            if (urlRegex.test(part)) {
                const displayText = part.length > 30 ? `${part.slice(0, 30)}...` : part;
                return (_jsx("a", { href: part, target: "_blank", rel: "noopener noreferrer", className: "text-blue-600 underline hover:text-blue-800", "aria-label": `Öppna länk: ${part} (öppnas i ny flik)`, title: part, children: displayText }, index));
            }
            return part;
        });
    }, []);
    // Component for expandable description with accessibility improvements
    const ExpandableDescription = useCallback(({ description, videoId }) => {
        const isExpanded = expandedDescriptions.has(videoId);
        const shouldTruncate = description.length > 100;
        if (!shouldTruncate) {
            return _jsx("p", { children: parseLinksInText(description) });
        }
        const displayText = isExpanded ? description : `${description.slice(0, 90)}...`;
        const buttonText = isExpanded ? 'Visa mindre' : 'Läs mer';
        return (_jsx("div", { children: _jsxs("p", { children: [parseLinksInText(displayText), _jsx("button", { type: "button", onClick: () => toggleDescription(videoId), className: "ml-2 cursor-pointer hover:underline", "aria-expanded": isExpanded, "aria-label": `${buttonText} för ${videoId}`, children: buttonText })] }) }));
    }, [expandedDescriptions, toggleDescription, parseLinksInText]);
    // Render function for each video card
    const renderVideoCard = useCallback((video, isFeaturedVideo = false) => {
        const videoTitle = `${isFeaturedVideo ? 'Huvudvideo: ' : ''}${video.title}`;
        return (_jsxs("div", { className: "group relative overflow-hidden", children: [_jsxs("div", { className: "relative aspect-video cursor-pointer", onClick: () => { }, role: "button", tabIndex: 0, "aria-label": `Spela ${videoTitle}`, onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            // TODO: handleVideoPlay(video.id);
                        }
                    }, children: [_jsx("img", { src: video.thumbnail, alt: `Miniatyrbild för ${video.title}`, className: "h-full w-full rounded-lg object-cover", loading: isFeaturedVideo ? 'eager' : 'lazy' }), isFeaturedVideo && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center rounded-lg", children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "50", height: "50", viewBox: "0 0 512 512", fill: "white", "aria-hidden": "true", children: _jsx("path", { d: "M204.8 371.2L358.4 256 204.8 140.8v230.4zM256 0C114.7 0 0 114.7 0 256s114.7 256 256 256 256-114.7 256-256S397.3 0 256 0zm0 460.8c-112.9 0-204.8-91.9-204.8-204.8S143.1 51.2 256 51.2 460.8 143.1 460.8 256 368.9 460.8 256 460.8z" }) }) })), !isFeaturedVideo && (_jsx("div", { className: "absolute right-2 bottom-2 rounded bg-black px-2 py-1 text-xs text-white shadow-lg", children: formatDuration(video.duration) }))] }), _jsxs("div", { className: "pt-4", children: [_jsx("h3", { className: "mb-2 text-xl font-medium", children: video.title }), _jsx("time", { dateTime: video.publishedAt, children: new Date(video.publishedAt).toLocaleDateString() }), isFeaturedVideo && _jsx(ExpandableDescription, { description: video.description, videoId: video.id })] })] }, video.id));
    }, [/* handleVideoPlay, */ formatDuration, ExpandableDescription]);
    // Render the main component structure
    return (_jsxs("main", { className: "text-base-foreground bg-base-background container mx-auto px-4 py-8", children: [featuredVideo && (_jsx("section", { className: "mb-8", "aria-label": "Huvudvideo", children: renderVideoCard(featuredVideo, true) })), displayedVideos.length > 0 && (_jsx("section", { "aria-label": "Videospellista", children: _jsx("div", { className: "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2", children: displayedVideos.map((video) => renderVideoCard(video, false)) }) })), shouldShowLoadMoreButton && (_jsx("div", { className: "mt-8 flex justify-center", children: _jsx(Button, { variant: "outline", onClick: handleLoadMoreVideos, "aria-label": `${loadMoreButtonText} - Visa fler videor`, children: loadMoreButtonText }) })), shouldShowYouTubeButton && (_jsx("div", { className: "mt-8 flex justify-center", children: _jsx(Button, { variant: "outline", render: _jsx("a", { href: `https://www.youtube.com/playlist?list=${categoryKey}`, rel: "noreferrer", target: "_blank", title: "Se fler program p\u00E5 youtube", "aria-label": "\u00D6ppna hela spellistan p\u00E5 YouTube (\u00F6ppnas i ny flik)" }), children: "Se fler program p\u00E5 youtube" }) }))] }));
}
//# sourceMappingURL=VideoPlaylist.js.map